import { BaseEffect } from '../BaseEffect.js';

/**
 * Canyon effect implementation
 * Based on "Canyon" by Inigo Quilez
 * Creates a procedural canyon landscape with dynamic lighting and atmospheric effects
 */
export class CanyonEffect extends BaseEffect {
    constructor(shaderManager) {
        const defaultParams = {
            speed: 0.5,           // Camera movement speed
            height: 1.2,          // Terrain height scale
            detail: 0.7,          // Terrain detail level
            roughness: 0.8,       // Surface roughness
            sunIntensity: 1.2,    // Sun light intensity
            atmosphereColor: [0.8, 0.9, 1.0], // Sky color
            groundColor: [0.75, 0.5, 0.3],    // Ground base color
            blendAmount: 0.85,    // Amount to blend with input image
            animate: true         // Whether to animate the camera
        };

        super('canyon', shaderManager, defaultParams);
    }

    async init() {
        const vertexSource = `
            attribute vec2 a_position;
            attribute vec2 a_texCoord;
            varying vec2 v_texcoord;
            
            void main() {
                gl_Position = vec4(a_position, 0, 1);
                v_texcoord = a_texCoord;
            }
        `;

        const fragmentSource = `
            precision highp float;
            
            uniform sampler2D u_image;
            uniform float u_time;
            uniform vec2 u_resolution;
            uniform float u_speed;
            uniform float u_height;
            uniform float u_detail;
            uniform float u_roughness;
            uniform float u_sunIntensity;
            uniform vec3 u_atmosphereColor;
            uniform vec3 u_groundColor;
            uniform float u_blendAmount;
            
            varying vec2 v_texcoord;

            const mat3 m = mat3(0.00, 0.80, 0.60,
                              -0.80, 0.36, -0.48,
                              -0.60, -0.48, 0.64);

            float hash(float n) {
                return fract(sin(n) * 43758.5453123);
            }

            float noise(vec3 x) {
                vec3 p = floor(x);
                vec3 f = fract(x);
                f = f * f * (3.0 - 2.0 * f);
                float n = p.x + p.y * 157.0 + 113.0 * p.z;
                return mix(mix(mix(hash(n + 0.0), hash(n + 1.0), f.x),
                             mix(hash(n + 157.0), hash(n + 158.0), f.x), f.y),
                         mix(mix(hash(n + 113.0), hash(n + 114.0), f.x),
                             mix(hash(n + 270.0), hash(n + 271.0), f.x), f.y), f.z);
            }

            float fbm(vec3 p) {
                float f = 0.0;
                float amp = 0.5;
                float freq = 1.0;
                
                // Use detail parameter to control number of octaves
                for(int i = 0; i < 5; i++) {
                    if (float(i) >= 2.0 + 3.0 * u_detail) break;
                    f += amp * noise(p * freq);
                    freq *= 2.02;
                    p = m * p;
                    amp *= 0.5;
                }
                
                return f;
            }

            float terrain(vec2 pos) {
                // Adjust scale based on detail
                vec2 p = pos * (0.003 - 0.001 * u_detail);
                float a = 0.0;
                float b = 1.0;
                
                // Use detail to control iterations
                for(int i = 0; i < 5; i++) {
                    if (float(i) >= 2.0 + 3.0 * u_detail) break;
                    float n = fbm(vec3(p, 0.0));
                    a += b * n;
                    b *= 0.5;
                    p *= 2.0;
                }
                
                // Enhance detail influence
                float detailMult = 1.0 + u_detail * 0.5;
                return u_height * (a * 20.0 * detailMult);
            }

            vec3 normal(vec2 pos) {
                vec2 eps = vec2(1.0, 0.0);
                float h = terrain(pos);
                vec3 nor = vec3(0.0);
                nor.x = terrain(pos + eps.xy) - terrain(pos - eps.xy);
                nor.y = 2.0 * eps.x;
                nor.z = terrain(pos + eps.yx) - terrain(pos - eps.yx);
                return normalize(nor);
            }

            // Improved overlay blend mode
            vec3 overlay(vec3 base, vec3 blend) {
                return mix(
                    2.0 * base * blend,
                    1.0 - 2.0 * (1.0 - base) * (1.0 - blend),
                    step(0.5, base)
                );
            }

            void main() {
                // Sample the input texture first
                vec4 inputColor = texture2D(u_image, v_texcoord);
                
                vec2 p = (2.0 * gl_FragCoord.xy - u_resolution.xy) / min(u_resolution.y, u_resolution.x);
                
                // Camera movement
                float time = u_time * u_speed;
                vec3 ro = vec3(50.0 * cos(0.2 * time), 15.0, 50.0 * sin(0.2 * time));
                vec3 ta = vec3(0.0, 0.0, 0.0);
                
                // Camera matrix
                vec3 ww = normalize(ta - ro);
                vec3 uu = normalize(cross(ww, vec3(0.0, 1.0, 0.0)));
                vec3 vv = normalize(cross(uu, ww));
                vec3 rd = normalize(p.x * uu + p.y * vv + 2.0 * ww);

                // Sky color
                vec3 col = mix(u_atmosphereColor, vec3(0.2, 0.5, 1.0), rd.y);
                
                // Raymarch terrain
                float t = 0.0;
                float tmax = 200.0;
                float h = 1.0;
                float precis = 0.001;
                
                for(int i = 0; i < 128; i++) {
                    vec3 pos = ro + t * rd;
                    h = pos.y - terrain(pos.xz);
                    if(abs(h) < precis * t || t > tmax) break;
                    t += h * 0.5;
                }

                if(t < tmax) {
                    vec3 pos = ro + t * rd;
                    vec3 nor = normal(pos.xz);
                    
                    // Enhanced material
                    vec3 mate = u_groundColor;
                    mate *= 0.3 + 0.7 * mix(1.0, fbm(0.05 * pos), u_roughness);
                    
                    // Enhanced lighting
                    vec3 sun_dir = normalize(vec3(-0.8, 0.4, -0.3));
                    float sun = clamp(dot(nor, sun_dir), 0.0, 1.0);
                    float sky = clamp(0.5 + 0.5 * nor.y, 0.0, 1.0);
                    float amb = clamp(0.5 + 0.5 * nor.y, 0.0, 1.0);
                    
                    vec3 lin = vec3(0.0);
                    lin += sun * vec3(1.64, 1.27, 0.99) * u_sunIntensity * 1.5;  // Increased sun intensity
                    lin += sky * vec3(0.16, 0.20, 0.28) * 1.2;  // Enhanced sky light
                    lin += amb * vec3(0.10, 0.15, 0.20) * 1.3;  // Enhanced ambient
                    
                    col = mate * lin;
                    
                    // Enhanced fog
                    float fog = 1.0 - exp(-0.0005 * t * t);
                    col = mix(col, u_atmosphereColor, fog * 0.7);  // Reduced fog influence
                }

                // Enhance the canyon colors
                col = pow(col, vec3(0.8));  // Reduced gamma for more visibility
                col *= 1.5;  // Increased overall brightness
                
                // Get luminance of input image to influence blend
                float luminance = dot(inputColor.rgb, vec3(0.299, 0.587, 0.114));
                
                // Create height influence based on terrain
                float heightInfluence = clamp(1.0 - t/tmax, 0.0, 1.0);
                
                // Complex blending
                vec3 blendedColor = mix(
                    overlay(inputColor.rgb, col),
                    col,
                    heightInfluence * u_blendAmount
                );
                
                // Final color enhancement
                blendedColor *= 1.2;  // Boost final result
                blendedColor = clamp(blendedColor, 0.0, 1.0);
                
                gl_FragColor = vec4(blendedColor, 1.0);
            }
        `;

        await super.init(vertexSource, fragmentSource);
    }

    setupUniforms() {
        super.setupUniforms();
        
        this.addUniform('u_speed', { type: '1f' });
        this.addUniform('u_height', { type: '1f' });
        this.addUniform('u_detail', { type: '1f' });
        this.addUniform('u_roughness', { type: '1f' });
        this.addUniform('u_sunIntensity', { type: '1f' });
        this.addUniform('u_atmosphereColor', { type: '3fv' });
        this.addUniform('u_groundColor', { type: '3fv' });
        this.addUniform('u_blendAmount', { type: '1f' });
    }

    updateUniforms(uniforms) {
        super.updateUniforms(uniforms);
        
        Object.assign(uniforms, {
            u_speed: { value: this.params.speed, type: '1f' },
            u_height: { value: this.params.height, type: '1f' },
            u_detail: { value: this.params.detail, type: '1f' },
            u_roughness: { value: this.params.roughness, type: '1f' },
            u_sunIntensity: { value: this.params.sunIntensity, type: '1f' },
            u_atmosphereColor: { value: this.params.atmosphereColor, type: '3fv' },
            u_groundColor: { value: this.params.groundColor, type: '3fv' },
            u_blendAmount: { value: this.params.blendAmount, type: '1f' }
        });

        if (!this.params.animate) {
            uniforms.u_time.value = 0;
        }
    }

    getUIConfig() {
        return {
            blendAmount: {
                min: 0,
                max: 1,
                step: 0.05,
                label: 'Effect Strength'
            },
            sunIntensity: {
                min: 0.5,
                max: 2.5,
                step: 0.1,
                label: 'Sun Intensity'
            },
            height: {
                min: 0.5,
                max: 2.5,
                step: 0.1,
                label: 'Terrain Height'
            },
            detail: {
                min: 0.2,
                max: 1,
                step: 0.1,
                label: 'Detail Level'
            },
            roughness: {
                min: 0.2,
                max: 1,
                step: 0.1,
                label: 'Surface Roughness'
            },
            speed: {
                min: 0,
                max: 1.5,
                step: 0.1,
                label: 'Camera Speed'
            },
            animate: {
                label: 'Animate Camera'
            }
        };
    }
} 